/**
 * How a port is physically reached, normalized into one shape so the ports list
 * and the port detail present it identically.
 *
 * A port can have a cross connect, a carrier access, or both — 23 and 46 in
 * production have both. **Carrier access wins**: when the port is reached by a
 * last mile, that is its physical connection, and the cross connect is an
 * internal detail that would only compete for the same cell. So a port resolves
 * to exactly one *type*, which may still carry several *ids* (port `10-C09582`
 * is reached by IENTC and MCM at once).
 */

import {
    formatCarrierAccessNames,
    type CarrierAccess,
} from "./carrierAccess";

export type PhysicalConnectionKind = "cross-connect" | "carrier-access";

export interface PhysicalConnectionEntry {
    /** Identifier or carrier name. */
    id: string;
    /**
     * Media and speed, for a cross connect only (`SMF, 10G`). The port detail
     * appends it to the value; the list omits it, matching what the old single
     * column showed. Carriers carry no detail: the backend stores their circuit
     * and service request but does not send them.
     */
    detail: string | null;
}

export interface PhysicalConnection {
    kind: PhysicalConnectionKind;
    /** What kind of connection this is: `Cross Connect` / `Carrier Access`. */
    typeLabel: string;
    /**
     * What the identifiers are called for this kind — `ID` for a cross connect,
     * `Carrier` for a carrier access. Lives here rather than in the cell so the
     * wording travels with the precedence rule instead of being hardcoded in a
     * render branch.
     */
    valueLabel: string;
    /** One entry per id; more than one only for a multi-carrier port. */
    entries: PhysicalConnectionEntry[];
}

/**
 * Cross connect descriptions carry a trailing `KIO XC ID:` fragment that is
 * noise once the id is already in its own cell. Keep the first two segments,
 * which is where the media and speed live (`SMF, 10G, KIO XC ID:` -> `SMF, 10G`).
 */
const cleanCrossConnectDetails = (details?: string | null): string | null => {
    if (!details) return null;
    const kept = details.split(",").slice(0, 2).join(",").trim();
    return kept === "" ? null : kept;
};

/**
 * Resolve the port's physical connection, or null when it has none.
 *
 * Callers pass whatever their payload holds — the list response exposes the
 * cross connect as a flat id, the detail response as an object — so the shape
 * difference stays at the call site instead of leaking in here.
 */
export const buildPhysicalConnection = ({
    crossConnectId,
    crossConnectDetails,
    carriers,
}: {
    crossConnectId?: string | null;
    crossConnectDetails?: string | null;
    carriers?: CarrierAccess[];
}): PhysicalConnection | null => {
    const carrierList = carriers ?? [];

    // Carrier access takes precedence over the cross connect.
    const names = formatCarrierAccessNames(carrierList);
    if (names !== "") {
        // `formatCarrierAccessNames` already orders last mile first and drops
        // duplicate names, so the split is the display order.
        return {
            kind: "carrier-access",
            typeLabel: "Carrier Access",
            valueLabel: "Carrier",
            entries: names.split(", ").map((name) => ({ id: name, detail: null })),
        };
    }

    if (crossConnectId) {
        return {
            kind: "cross-connect",
            typeLabel: "Cross Connect",
            valueLabel: "ID",
            entries: [
                {
                    id: crossConnectId,
                    detail: cleanCrossConnectDetails(crossConnectDetails),
                },
            ],
        };
    }

    return null;
};

/** Type label for sorting and filtering; empty when the port has none. */
export const physicalConnectionType = (
    connection: PhysicalConnection | null
): string => connection?.typeLabel ?? "";

/** Joined identifiers for sorting and filtering. */
export const physicalConnectionIds = (
    connection: PhysicalConnection | null
): string =>
    (connection?.entries ?? []).map((entry) => entry.id).join(", ");
